"use client"

import { useState, useEffect } from "react"
import { CheckCircle2, Upload, AlertCircle, ImagePlus } from "lucide-react"
import { supabase } from "../../../supabaseClient" // ajuste o caminho se necessário

const BUCKET = "temp-logos"

// Fotos tiradas na hora pela câmera do celular costumam vir bem maiores
// (às vezes 10MB+, e em alguns iPhones em formato HEIC) do que uma imagem
// já existente escolhida da galeria — isso é o motivo mais provável de
// "galeria funciona, câmera não" (upload grande demais/lento, ou formato
// que trava em algum lugar). Redesenha num canvas e comprime pra JPEG antes
// de enviar, resolvendo os dois problemas de uma vez.
async function compressImage(file: File, maxDim = 1600, quality = 0.85): Promise<File> {
  try {
    const bitmap = await createImageBitmap(file)
    const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height))
    const w = Math.round(bitmap.width * scale)
    const h = Math.round(bitmap.height * scale)

    const canvas = document.createElement("canvas")
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext("2d")
    if (!ctx) return file
    ctx.drawImage(bitmap, 0, 0, w, h)

    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), "image/jpeg", quality)
    )
    if (!blob) return file

    return new File([blob], "logo.jpg", { type: "image/jpeg" })
  } catch (e) {
    // Se a compressão falhar por qualquer motivo (ex: formato não suportado
    // por createImageBitmap), manda o arquivo original mesmo — melhor
    // tentar do que travar por completo.
    console.warn("[compressImage] não foi possível comprimir, enviando original:", e)
    return file
  }
}

async function uploadFile(sessionId: string, file: File): Promise<{ ok: boolean; error?: string }> {
  const compressed = await compressImage(file)
  const ext = compressed.name.split(".").pop() || "jpg"
  // Nome único por envio — não sobrescreve o anterior, e permite o desktop
  // reconhecer com certeza cada foto nova (em vez de depender de metadata
  // de "modificado em" que pode não vir populada de forma confiável).
  const uniqueName = `logo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`
  const path = `${sessionId}/${uniqueName}`
  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(path, compressed, { upsert: false, contentType: compressed.type || "image/jpeg" })
  return { ok: !error, error: error?.message }
}

// ─── Component ────────────────────────────────────────────────────────────────

export function UploadLogoMobile({ sessionId }: { sessionId: string }) {
  const [status, setStatus] = useState<"idle" | "uploading" | "done" | "error">("idle")
  const [preview, setPreview] = useState<string | null>(null)
  const [errorMsg, setErrorMsg] = useState("")

  // Esconde o loading screen do simulador — não faz sentido no mobile
  useEffect(() => {
    const hide = () => {
      const el = document.getElementById("loadingNew")
      if (el) el.style.display = "none"
    }
    hide()
    // Tenta de novo após um tick caso o elemento ainda não exista
    const t = setTimeout(hide, 500)
    return () => clearTimeout(t)
  }, [])

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = "" // limpa já aqui, pra poder escolher o mesmo arquivo de novo depois
    if (!file) return

    // Evita duas seleções disparando upload ao mesmo tempo (ex: toque duplo)
    if (status === "uploading") return

    const reader = new FileReader()
    reader.onload = ev => setPreview(ev.target?.result as string)
    reader.readAsDataURL(file)

    setStatus("uploading")
    setErrorMsg("")

    try {
      const { ok, error } = await uploadFile(sessionId, file)
      if (!ok) throw new Error(error || "Falha no upload")
      setStatus("done")
    } catch (err: any) {
      setStatus("error")
      setErrorMsg(err?.message ?? "Erro desconhecido")
    }
  }

  // Mesmas duas opções (câmera + galeria) em qualquer estado — antes, depois
  // do primeiro envio só sobrava uma opção genérica sem câmera.
  const UploadOptions = () => (
    <div className="w-full max-w-sm flex flex-col gap-3">
      <label className="flex items-center gap-4 w-full px-5 py-4 rounded-2xl border-2 border-dashed border-border hover:border-primary bg-secondary/20 hover:bg-primary/5 cursor-pointer transition-all touch-manipulation">
        <div className="w-12 h-12 rounded-xl bg-secondary/50 flex items-center justify-center flex-shrink-0">
          <svg className="w-6 h-6 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/>
            <circle cx="12" cy="13" r="3"/>
          </svg>
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">Tirar foto</p>
          <p className="text-xs text-muted-foreground mt-0.5">Usar a câmera do celular</p>
        </div>
        <input key={`camera-${status}`} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFile} />
      </label>

      <label className="flex items-center gap-4 w-full px-5 py-4 rounded-2xl border-2 border-dashed border-border hover:border-primary bg-secondary/20 hover:bg-primary/5 cursor-pointer transition-all touch-manipulation">
        <div className="w-12 h-12 rounded-xl bg-secondary/50 flex items-center justify-center flex-shrink-0">
          <ImagePlus className="w-6 h-6 text-muted-foreground" />
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">Escolher da galeria</p>
          <p className="text-xs text-muted-foreground mt-0.5">PNG, JPG, SVG</p>
        </div>
        <input key={`gallery-${status}`} type="file" accept="image/*" className="hidden" onChange={handleFile} />
      </label>
    </div>
  )

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6 gap-6">

      <div className="text-center space-y-1">
        <h1 className="text-xl font-bold text-foreground">Enviar Logo</h1>
        <p className="text-sm text-muted-foreground">Selecione a imagem do escudo ou logo</p>
      </div>

      {status === "done" && (
        <div className="w-full max-w-sm space-y-4 animate-in fade-in duration-300">
          <div className="rounded-2xl border-2 border-green-500/40 bg-green-500/10 p-6 flex flex-col items-center gap-3 text-center">
            <CheckCircle2 className="w-12 h-12 text-green-400" />
            <p className="font-semibold text-foreground">Logo enviada!</p>
            <p className="text-sm text-muted-foreground">Volte para o simulador no computador.</p>
          </div>
          {preview && (
            <div className="rounded-2xl border border-border/40 bg-secondary/20 p-4 flex items-center justify-center">
              <img src={preview} alt="logo" className="max-h-32 object-contain" />
            </div>
          )}
          <p className="text-xs text-muted-foreground text-center">Quer trocar a imagem? Escolha outra abaixo:</p>
          <UploadOptions />
        </div>
      )}

      {status === "uploading" && (
        <div className="w-full max-w-sm rounded-2xl bg-secondary/20 border border-border/40 p-8 flex flex-col items-center gap-4">
          {preview && <img src={preview} alt="" className="max-h-28 object-contain rounded-xl opacity-70" />}
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 rounded-full border-2 border-primary border-t-transparent animate-spin" />
            <span className="text-sm text-muted-foreground">Enviando…</span>
          </div>
        </div>
      )}

      {status === "error" && (
        <div className="w-full max-w-sm space-y-4">
          <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-5 flex flex-col items-center gap-2 text-center">
            <AlertCircle className="w-8 h-8 text-red-400" />
            <p className="text-sm font-semibold text-red-400">Erro ao enviar</p>
            {errorMsg && <p className="text-xs text-muted-foreground">{errorMsg}</p>}
          </div>
          <UploadOptions />
        </div>
      )}

      {status === "idle" && <UploadOptions />}
    </div>
  )
}